import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, UserCheck } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/Card';
import Button from '../../../../components/ui/Button';
import { Switch } from '../../../../components/ui/Switch';
import { appDateTime } from '@/utils/appDateFormat';
import { parseApiUtc } from '@/utils/apiDate';
import {
    APPROVAL_BYPASS_REASON_MAX,
    type ApprovalRouting,
    getApprovalRouting,
    setApprovalBypass,
} from '../../../../api/General/approvalRouting';

// CO-814 — "who approves this employee" + HR's approval bypass.
// Rendered by Personal/index.tsx for HR (any profile, incl. their own: bypass then read-only) and, read-only
// (no bypass section), for Chief on another employee's profile. The server enforces
// HrOnly, same company (another company's id is a 404) and "HR cannot bypass themselves" (canEditBypass).
// Layout mirrors MfaResetSection / DataExportSection so the Personal tab reads as one family of cards.
// Progressive disclosure only: turning bypass on opens an inline reason field, turning it off an inline
// confirm. No modal.

interface Props {
    userId: string;
    /** Chief viewer (CO-814 review fix): route + approver names only, no bypass section. */
    readOnly?: boolean;
    /** The viewer is looking at their own profile (HR): Unroutable reads as "assign yourself a manager". */
    isOwnProfile?: boolean;
}

type Mode = 'idle' | 'enabling' | 'disabling';

/** Server error descriptions are i18n keys; anything else falls back to the generic message. */
function useErrorText() {
    const { t } = useTranslation();
    return useCallback(
        (description?: string | null) =>
            description
                ? t(description, { defaultValue: t('approvalRouting.errors.generic') })
                : t('approvalRouting.errors.generic'),
        [t],
    );
}

function httpStatusOf(err: unknown): number | undefined {
    const e = err as { status?: unknown; __httpStatus?: unknown } | null;
    const s = e?.__httpStatus ?? e?.status;
    return typeof s === 'number' ? s : undefined;
}

function firstErrorDescription(err: unknown): string | undefined {
    const e = err as { errors?: { description?: string }[] } | null;
    return e?.errors?.[0]?.description;
}

const ApprovalRoutingSection: React.FC<Props> = ({ userId, readOnly = false, isOwnProfile = false }) => {
    const { t } = useTranslation();
    const errorText = useErrorText();

    const [routing, setRouting] = useState<ApprovalRouting | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    // A 403/404 on load means this viewer has no business seeing the card at all: render nothing.
    const [forbidden, setForbidden] = useState(false);

    const [mode, setMode] = useState<Mode>('idle');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const res = await getApprovalRouting(userId);
            if (res.errors?.length) {
                setLoadError(errorText(res.errors[0]?.description));
                return;
            }
            setRouting(res.payload);
        } catch (err) {
            const status = httpStatusOf(err);
            if (status === 403 || status === 404) {
                setForbidden(true);
                return;
            }
            setLoadError(errorText(firstErrorDescription(err)));
        } finally {
            setLoading(false);
        }
    }, [userId, errorText]);

    useEffect(() => {
        setRouting(null);
        setForbidden(false);
        setMode('idle');
        setReason('');
        setError(null);
        void load();
    }, [load]);

    const submit = async (enabled: boolean) => {
        setBusy(true);
        setError(null);
        try {
            const res = await setApprovalBypass(userId, enabled ? { enabled, reason: reason.trim() } : { enabled });
            if (res.errors?.length) {
                setError(errorText(res.errors[0]?.description));
                return;
            }
            setRouting(res.payload);
            setMode('idle');
            setReason('');
        } catch (err) {
            setError(errorText(firstErrorDescription(err)));
        } finally {
            setBusy(false);
        }
    };

    if (forbidden) return null;

    const onToggle = (next: boolean) => {
        if (!routing || busy) return;
        setError(null);
        if (next === routing.approvalBypass) {
            setMode('idle');
            return;
        }
        setMode(next ? 'enabling' : 'disabling');
        if (next) setReason('');
    };

    const cancel = () => {
        setMode('idle');
        setReason('');
        setError(null);
    };

    const switchChecked = routing
        ? mode === 'enabling' ? true : mode === 'disabling' ? false : routing.approvalBypass
        : false;

    const trimmedReason = reason.trim();
    const reasonTooLong = reason.length > APPROVAL_BYPASS_REASON_MAX;

    const names = routing?.approverNames ?? [];
    const extra = routing ? Math.max(0, routing.approverCount - names.length) : 0;
    const namesText = names.join(t('approvalRouting.nameSeparator', { defaultValue: ', ' }));

    const renderRoute = (r: ApprovalRouting) => {
        if (r.route === 'Unroutable') {
            return (
                <div
                    className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                    data-testid="approval-routing-route"
                    data-route={r.route}
                    role="status"
                >
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                    <p className="min-w-0">
                        {isOwnProfile
                            ? t('approvalRouting.waitingFor.unroutableSelfHr')
                            : t('approvalRouting.card.routeUnroutable')}
                    </p>
                </div>
            );
        }
        // ChiefFallback (CO-814): no manager and no other HR user, so the company's Chief users decide;
        // approverNames are those Chiefs.
        const lead = r.route === 'HrFallback'
            ? t('approvalRouting.card.routeHrFallback')
            : r.route === 'ChiefFallback'
                ? t('approvalRouting.card.routeChiefFallback')
                : t('approvalRouting.card.routeManager');
        return (
            <div className="text-sm" data-testid="approval-routing-route" data-route={r.route}>
                <p className="text-muted-foreground">{lead}</p>
                <p className="mt-1 font-medium text-foreground break-words" data-testid="approval-routing-approvers">
                    {namesText}
                    {extra > 0 && (
                        <span className="font-normal text-muted-foreground">
                            {' '}{t('approvalRouting.card.andMore', { count: extra })}
                        </span>
                    )}
                </p>
            </div>
        );
    };

    const renderBypassState = (r: ApprovalRouting) => {
        if (!r.approvalBypass) {
            return (
                <p className="text-sm text-muted-foreground" data-testid="approval-bypass-state" data-enabled="false">
                    {t('approvalRouting.bypass.stateOff')}
                </p>
            );
        }
        const setOn = parseApiUtc(r.approvalBypassSetOnUtc);
        return (
            <div
                className="rounded-md border border-border bg-muted/40 px-3 py-2.5 text-sm"
                data-testid="approval-bypass-state"
                data-enabled="true"
            >
                <p className="text-foreground">
                    {t('approvalRouting.bypass.stateOn', {
                        date: setOn ? appDateTime(setOn) : t('approvalRouting.bypass.unknown'),
                        name: r.approvalBypassSetByName || t('approvalRouting.bypass.unknown'),
                    })}
                </p>
                {r.approvalBypassReason && (
                    <p className="mt-1 text-muted-foreground break-words whitespace-pre-line">
                        <span className="font-medium text-foreground">{t('approvalRouting.bypass.reasonLabel')}</span>{' '}
                        {r.approvalBypassReason}
                    </p>
                )}
            </div>
        );
    };

    return (
        <Card data-testid="approval-routing-card">
            <CardContent className="p-6">
                <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center">
                        <UserCheck className="w-5 h-5 text-primary" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold">{t('approvalRouting.card.title')}</h2>
                        <p className="text-sm text-muted-foreground">{t('approvalRouting.card.description')}</p>
                    </div>
                </div>

                {loading && !routing && (
                    <div className="mt-4 space-y-2" aria-busy="true" aria-label={t('approvalRouting.card.loading')}>
                        <div className="h-4 w-40 rounded bg-muted animate-pulse" />
                        <div className="h-4 w-64 max-w-full rounded bg-muted animate-pulse" />
                    </div>
                )}

                {loadError && !routing && (
                    <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between p-3 rounded-md text-sm bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-200 dark:border-rose-900" role="alert" data-testid="approval-routing-load-error">
                        <p className="min-w-0">{loadError}</p>
                        <Button size="sm" variant="outline" onClick={() => void load()}>
                            {t('approvalRouting.card.retry')}
                        </Button>
                    </div>
                )}

                {routing && (
                    <>
                        <div className="mt-4">{renderRoute(routing)}</div>

                        {!readOnly && (
                        <div className="mt-5 pt-5 border-t border-border/60 space-y-3" data-testid="approval-bypass-section">
                            <Switch
                                checked={switchChecked}
                                onCheckedChange={onToggle}
                                disabled={!routing.canEditBypass || busy || mode !== 'idle'}
                                label={t('approvalRouting.bypass.toggleLabel')}
                                description={t('approvalRouting.bypass.help')}
                                data-testid="approval-bypass-toggle"
                            />

                            {!routing.canEditBypass && (
                                <p className="text-xs text-muted-foreground" data-testid="approval-bypass-self">
                                    {t('approvalRouting.bypass.cannotEditSelf')}
                                </p>
                            )}

                            <div aria-live="polite" className="space-y-3">
                                {mode === 'enabling' && (
                                    <div className="p-3 rounded-md border border-border bg-muted/40 space-y-2">
                                        <label htmlFor={`approval-bypass-reason-${userId}`} className="block text-sm font-medium text-foreground">
                                            {t('approvalRouting.bypass.reasonPrompt')}
                                        </label>
                                        <textarea
                                            id={`approval-bypass-reason-${userId}`}
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                            rows={3}
                                            autoFocus
                                            disabled={busy}
                                            maxLength={APPROVAL_BYPASS_REASON_MAX}
                                            aria-required="true"
                                            aria-describedby={`approval-bypass-reason-count-${userId}`}
                                            placeholder={t('approvalRouting.bypass.reasonPlaceholder')}
                                            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:opacity-50"
                                            data-testid="approval-bypass-reason"
                                        />
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
                                            <p className="text-xs text-muted-foreground">
                                                {t('approvalRouting.bypass.recordedNote')}
                                                {' '}
                                                <span id={`approval-bypass-reason-count-${userId}`} className="tabular-nums whitespace-nowrap">
                                                    {t('approvalRouting.bypass.reasonCount', {
                                                        used: reason.length,
                                                        max: APPROVAL_BYPASS_REASON_MAX,
                                                    })}
                                                </span>
                                            </p>
                                            <div className="flex flex-wrap gap-2 shrink-0">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    disabled={busy}
                                                    onClick={cancel}
                                                    data-testid="approval-bypass-cancel"
                                                >
                                                    {t('approvalRouting.bypass.cancel')}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    loading={busy}
                                                    disabled={!trimmedReason || reasonTooLong}
                                                    onClick={() => void submit(true)}
                                                    data-testid="approval-bypass-confirm"
                                                >
                                                    {t('approvalRouting.bypass.confirmOn')}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {mode === 'disabling' && (
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between p-3 rounded-md border border-border bg-muted/40">
                                        <p className="text-sm text-foreground min-w-0">{t('approvalRouting.bypass.confirmOffPrompt')}</p>
                                        <div className="flex flex-wrap gap-2 shrink-0">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                disabled={busy}
                                                onClick={cancel}
                                                data-testid="approval-bypass-cancel"
                                            >
                                                {t('approvalRouting.bypass.cancel')}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                loading={busy}
                                                onClick={() => void submit(false)}
                                                data-testid="approval-bypass-confirm"
                                            >
                                                {t('approvalRouting.bypass.confirmOff')}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {mode === 'idle' && renderBypassState(routing)}

                                {error && (
                                    <div className="p-3 rounded-md text-sm bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-200 dark:border-rose-900" role="alert" data-testid="approval-bypass-error">
                                        {error}
                                    </div>
                                )}
                            </div>
                        </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default ApprovalRoutingSection;
